// Ch78 · THE MOVE-IN CALENDAR — pure model, no React.
//
// Grant (2026-09-08): "a NICE LOOKING calendar or TIMELINE … interactive …
// visualize MOVE-IN DATES."
//
// Two projections of the same people the work list holds:
//
//   THE YEAR STRIP — twelve stacked bars, one per month. Height is how many
//   people move in that month, the stack is the scorer's read of them. It is
//   the year at a glance and the month picker in one control.
//
//   THE GRID — a real calendar month (or a single week), Sunday-first, with
//   leading and trailing days from the neighbouring months shown greyed so the
//   weeks are whole. Every person is a pill on the day they said they are
//   moving. Nothing is guessed onto a day: free text like "August" never
//   becomes a date — those people are in the no-date gutter with their words.
//
// The bar heights are computed by the component from `count`; this module
// stays a model so it can be tested against a fixed clock with no DOM.

import type { BoardPerson } from "./moveInBoard";
import type { TimelineTone } from "./leadsTimeline";

/** Calendar eligibility is separate from the work list, which retains Archive
 * for restoration. Apply after identity deduplication so an archived client
 * cannot reappear as their converted lead. */
export function isCalendarPerson(person: BoardPerson): boolean {
  if (person.kind === "client") {
    return Boolean(person.client) && person.client!.status !== "out_of_contact"
      && person.client!.is_synthetic !== true;
  }
  return Boolean(person.lead) && ["new", "converted"].includes(person.lead!.status)
    && person.lead!.is_synthetic !== true;
}

export type CalendarMode = "month" | "week";

const DAY_MS = 86_400_000;
const MONTH_LONG = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" });
const MONTH_SHORT = new Intl.DateTimeFormat("en-US", { month: "short" });
const DAY_SHORT = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });

export function midnightMs(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export interface CalendarDay {
  ms: number;
  /** "8" inside the anchor month; "Aug 30" for a padding day, so the eye can place it. */
  label: string;
  inMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
  /** Everyone whose move-in day is this day, biggest budget first. */
  people: BoardPerson[];
}

export interface CalendarModel {
  /** "September 2026" or "Sep 6 – Sep 12". */
  label: string;
  weeks: CalendarDay[][];
  /** People inside the anchor month (or the week), not the padding days. */
  count: number;
  budgetTotal: number;
  todayMs: number;
}

/** Sunday of the week containing `d`. */
function weekStart(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - d.getDay());
}

export function snapCalendarAnchor(d: Date, mode: CalendarMode): Date {
  return mode === "week" ? weekStart(d) : new Date(d.getFullYear(), d.getMonth(), 1);
}

export function shiftCalendarAnchor(anchor: Date, mode: CalendarMode, delta: number): Date {
  if (mode === "week") {
    return new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() + 7 * delta);
  }
  return new Date(anchor.getFullYear(), anchor.getMonth() + delta, 1);
}

function byDay(people: BoardPerson[]): Map<number, BoardPerson[]> {
  const map = new Map<number, BoardPerson[]>();
  for (const p of people) {
    if (p.dateMs === null) continue;
    const bucket = map.get(p.dateMs);
    if (bucket) bucket.push(p);
    else map.set(p.dateMs, [p]);
  }
  for (const bucket of map.values()) bucket.sort((a, b) => (b.budget ?? 0) - (a.budget ?? 0));
  return map;
}

/**
 * One cell of the month/week grid, with NO domain attached.
 *
 * PKT-0792 · Ch94. Grant pointed at this page — *"see how it combined multiple
 * ideas into widgets"* — and the packet says REUSE it, do not rebuild a
 * calendar. The posting calendar needs the same grid this one draws (padding
 * days from neighbouring months so the weeks are whole, today, weekends, the
 * "8" vs "Aug 30" labelling) but hangs REELS on the days, not people.
 *
 * So the grid construction is extracted here unchanged and `buildCalendar`
 * below now consumes it. That is reuse rather than a second implementation
 * that would drift on the first calendar bug either page hits.
 */
export interface CalendarCell {
  ms: number;
  /** "8" inside the anchor month; "Aug 30" for a padding day. */
  label: string;
  inMonth: boolean;
  isToday: boolean;
  isWeekend: boolean;
}

export interface CalendarGrid {
  /** "September 2026" or "Sep 6 – Sep 12". */
  label: string;
  weeks: CalendarCell[][];
  todayMs: number;
}

export function buildCalendarGrid(opts: {
  anchor: Date;
  mode: CalendarMode;
  now: Date;
}): CalendarGrid {
  const anchor = snapCalendarAnchor(opts.anchor, opts.mode);
  const todayMs = midnightMs(opts.now);

  const anchorMonth = anchor.getMonth();
  const anchorYear = anchor.getFullYear();
  let first: Date;
  let rows: number;
  if (opts.mode === "week") {
    first = anchor;
    rows = 1;
  } else {
    first = weekStart(anchor);
    const lastOfMonth = new Date(anchorYear, anchorMonth + 1, 0);
    const span = Math.round((midnightMs(lastOfMonth) - midnightMs(first)) / DAY_MS) + 1;
    rows = Math.ceil(span / 7);
  }

  const weeks: CalendarCell[][] = [];
  for (let w = 0; w < rows; w++) {
    const week: CalendarCell[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(first.getFullYear(), first.getMonth(), first.getDate() + w * 7 + i);
      const ms = d.getTime();
      // In week mode every day belongs to the window; in month mode the
      // padding days from the neighbouring months are shown but not counted.
      const inMonth =
        opts.mode === "week" ||
        (d.getMonth() === anchorMonth && d.getFullYear() === anchorYear);
      week.push({
        ms,
        label: inMonth && opts.mode === "month" ? String(d.getDate()) : DAY_SHORT.format(d),
        inMonth,
        isToday: ms === todayMs,
        isWeekend: d.getDay() === 0 || d.getDay() === 6,
      });
    }
    weeks.push(week);
  }

  const label =
    opts.mode === "week"
      ? `${DAY_SHORT.format(first)} – ${DAY_SHORT.format(new Date(first.getFullYear(), first.getMonth(), first.getDate() + 6))}`
      : MONTH_LONG.format(anchor);

  return { label, weeks, todayMs };
}

export function buildCalendar(
  people: BoardPerson[],
  opts: { anchor: Date; mode: CalendarMode; now: Date },
): CalendarModel {
  const grid = buildCalendarGrid(opts);
  const days = byDay(people);

  let count = 0;
  let budgetTotal = 0;
  const weeks: CalendarDay[][] = grid.weeks.map((week) =>
    week.map((cell) => {
      const dayPeople = days.get(cell.ms) ?? [];
      if (cell.inMonth) {
        count += dayPeople.length;
        for (const p of dayPeople) budgetTotal += p.budget ?? 0;
      }
      return { ...cell, people: dayPeople };
    }),
  );

  return { label: grid.label, weeks, count, budgetTotal, todayMs: grid.todayMs };
}

export interface StripMonth {
  /** 0–11. */
  index: number;
  startMs: number;
  label: string;
  count: number;
  byTone: Record<TimelineTone, number>;
  /** The month ended before today — dimmed, still clickable. */
  isPast: boolean;
  isAnchor: boolean;
}

/** The twelve bars above the grid: a calendar year of move-ins. */
export function buildYearStrip(
  people: BoardPerson[],
  opts: { year: number; anchorMonth: number; anchorYear: number; now: Date },
): StripMonth[] {
  const todayMs = midnightMs(opts.now);
  const months: StripMonth[] = [];
  for (let m = 0; m < 12; m++) {
    const start = new Date(opts.year, m, 1);
    const end = new Date(opts.year, m + 1, 1);
    months.push({
      index: m,
      startMs: start.getTime(),
      label: MONTH_SHORT.format(start),
      count: 0,
      byTone: { real: 0, uncertain: 0, fake: 0, unscored: 0 },
      isPast: end.getTime() <= todayMs,
      isAnchor: opts.anchorYear === opts.year && opts.anchorMonth === m,
    });
  }
  for (const p of people) {
    if (p.dateMs === null) continue;
    const d = new Date(p.dateMs);
    if (d.getFullYear() !== opts.year) continue;
    const bar = months[d.getMonth()];
    bar.count += 1;
    bar.byTone[p.tone] += 1;
  }
  return months;
}

export interface CalendarGutters {
  /** Move-in day passed, nobody placed. Newest-passed first. */
  pastDue: BoardPerson[];
  /** They gave words, not a date. Their words are kept verbatim. */
  noDate: BoardPerson[];
  /** Unclaimed and long past — hidden by default, never deleted. */
  expired: BoardPerson[];
}

const PLACED_STAGES = new Set(["payment_received", "closed"]);

/**
 * The three honest leftovers under the grid. `expiredKeys` comes from the work
 * list, which owns the definition of expiry — one definition, not two.
 */
export function buildGutters(
  people: BoardPerson[],
  expiredKeys: Set<string>,
  now: Date,
): CalendarGutters {
  const todayMs = midnightMs(now);
  const pastDue: BoardPerson[] = [];
  const noDate: BoardPerson[] = [];
  const expired: BoardPerson[] = [];
  for (const p of people) {
    if (expiredKeys.has(p.key)) {
      expired.push(p);
      continue;
    }
    if (p.dateMs === null) noDate.push(p);
    else if (p.dateMs < todayMs && !PLACED_STAGES.has(p.stage)) pastDue.push(p);
  }
  pastDue.sort((a, b) => (b.dateMs ?? 0) - (a.dateMs ?? 0));
  expired.sort((a, b) => (b.dateMs ?? 0) - (a.dateMs ?? 0));
  return { pastDue, noDate, expired };
}
